import { IconMoodSad, IconMoodSmile } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getDeliveryBadgeLabelKey } from "@/features/rating/helpers/delivery-badges";
import type { DeliveryRating } from "@/features/rating/types/user-rating.type";
import { cn } from "@/lib/utils";

const GOOD_RATING_THRESHOLD = 3;

export const DeliveryReview = ({
  delivery,
  inverted = false,
}: {
  delivery: DeliveryRating;
  inverted?: boolean;
}) => {
  const t = useTranslations("rating");
  const tDelivery = useTranslations("rating.rateDialog.delivery");
  const isGood = delivery.rating >= GOOD_RATING_THRESHOLD;
  const badges = delivery.badges ?? [];

  return (
    <Accordion className="w-full" collapsible type="single">
      <AccordionItem
        className={cn("border-b-0", !inverted && "mt-4")}
        value="delivery"
      >
        <AccordionTrigger
          className={cn(
            "px-4 py-2 font-medium md:px-6 md:py-4",
            inverted ? "bg-brand-grey1" : "bg-brand-grey-light",
          )}
        >
          {t("deliveryReview.title")}
        </AccordionTrigger>
        <AccordionContent
          className={cn(
            "flex flex-col gap-y-3 px-4 pt-4 pb-4 md:px-6",
            inverted && "bg-brand-grey-light",
          )}
        >
          <div className="flex items-center gap-x-1.5 text-sm">
            <span className="font-medium text-brand-green1">
              {t("deliveryReview.ratingLabel")}
            </span>
            <span className="flex items-center gap-x-1 font-medium">
              {isGood ? tDelivery("good") : tDelivery("bad")}
              {isGood ? (
                <IconMoodSmile className="h-4 w-4 text-brand-yellow" />
              ) : (
                <IconMoodSad className="h-4 w-4 text-brand-yellow" />
              )}
            </span>
          </div>

          {badges.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {badges.map((badge) => {
                const labelKey = getDeliveryBadgeLabelKey(badge);
                return (
                  <span
                    className="rounded-md bg-brand-white px-2 py-1 font-medium text-brand-green2 text-xs"
                    key={badge}
                  >
                    {labelKey ? tDelivery(`badges.${labelKey}`) : badge}
                  </span>
                );
              })}
            </div>
          )}

          {delivery.review && (
            <p className="text-brand-green1 text-sm">{delivery.review}</p>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
